import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Power, Star, RefreshCw, EyeOff, Eye, Image as ImageIcon, Loader2, Users, Check, Search } from "lucide-react";
import { Card, CardContent } from "@/components/ui/Card.tsx";
import { Button } from "@/components/ui/Button.tsx";
import { Badge } from "@/components/ui/Badge.tsx";
import { Spinner } from "@/components/ui/Spinner.tsx";
import { Modal } from "@/components/ui/Modal.tsx";
import { Input } from "@/components/ui/Input.tsx";
import { Select } from "@/components/ui/Select.tsx";
import { CastPanel, type SourcePersonaSeed } from "./CastPanel.tsx";
import { playablePreviewUrl } from "@/lib/voice-preview.ts";
import {
  adminApi,
  type CharacterListItem,
  type CharacterBrowseFilters,
  type CharacterGenerationInput,
} from "@/api/admin.ts";

/**
 * Admin character library (CR-004 Phase 5).
 *
 * Browse the generated character catalogue, inspect a character's sheet +
 * provider bindings, generate new characters, and drive the management actions
 * the Phase-3 API exposes (activate/deactivate, delist/relist, set gender
 * default, regenerate). A character is a provider-agnostic record with up to two
 * provider identities — a HeyGen look (talking-head) and a Kling reference pack
 * (in-scene motion); the capability badges reflect which the character has.
 *
 * The invariants live in the backend (one default per gender, strict-dual ready,
 * regeneratable-state guards); this screen drives them and reflects the result.
 */

const PAGE_SIZE = 24;

const STATE_VARIANT: Record<string, "success" | "warning" | "danger" | "muted"> = {
  ready: "success",
  pending: "warning",
  failed: "danger",
};

const emptyGenForm: CharacterGenerationInput & { displayName: string; voiceCatalogId: string } = {
  displayName: "",
  gender: "F",
  ageRange: "25-35",
  ethnicity: "",
  style: "casual",
  visualDescriptor: "",
  niche: "",
  physicalAttributes: {},
  personas: [],
  voiceCatalogId: "",
};

export function AdminCharactersPage() {
  const qc = useQueryClient();

  const [filters, setFilters] = useState<CharacterBrowseFilters>({ limit: PAGE_SIZE, offset: 0 });
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [showGenerate, setShowGenerate] = useState(false);
  const [genForm, setGenForm] = useState(emptyGenForm);
  const [showRoster, setShowRoster] = useState(false);
  // Semantic casting: the arc being cast (opens CastPanel). Set from the "Cast an arc" entry point;
  // in practice AdminResearchPage passes a real arc — here we allow casting by typed persona too.
  const [castingArc, setCastingArc] = useState<{ circleKey: string; label?: string; seed?: SourcePersonaSeed } | null>(null);

  // Open the Generate modal pre-filled from a source persona (the create-in-flow from CastPanel).
  const openGenerateFromPersona = (seed: SourcePersonaSeed) => {
    setGenForm({
      ...emptyGenForm,
      gender: seed.gender === "M" || seed.gender === "F" ? seed.gender : emptyGenForm.gender,
      ageRange: seed.ageRange || emptyGenForm.ageRange,
      visualDescriptor: seed.description || "",
    });
    setCastingArc(null);       // close the cast panel; the new character will be castable after activate
    setShowGenerate(true);
  };

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "characters", filters],
    queryFn: () => adminApi.browseCharacters(filters),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["admin", "characters"] });
  const fail = (err: unknown, fallback: string) => {
    setNotice(null);
    setActionError((err as { message?: string })?.message || fallback);
  };
  const ok = (msg: string) => { setActionError(null); setNotice(msg); invalidate(); };

  const activate = useMutation({
    mutationFn: (c: CharacterListItem) =>
      c.isEnabled ? adminApi.deactivateCharacter(c.stockId) : adminApi.activateCharacter(c.stockId),
    onSuccess: (_r, c) => ok(c.isEnabled ? "Character disabled." : "Character enabled."),
    onError: (err) => fail(err, "Could not change enabled state"),
  });

  const delist = useMutation({
    mutationFn: (c: CharacterListItem) =>
      c.isDelisted ? adminApi.relistCharacter(c.stockId) : adminApi.delistCharacter(c.stockId),
    onSuccess: (_r, c) => ok(c.isDelisted ? "Character relisted." : "Character delisted."),
    onError: (err) => fail(err, "Could not change listing"),
  });

  const makeDefault = useMutation({
    mutationFn: (c: CharacterListItem) => adminApi.setCharacterDefault(c.stockId),
    onSuccess: () => ok("Set as the gender default."),
    onError: (err) => fail(err, "Could not set the default"),
  });

  const regenerate = useMutation({
    mutationFn: (c: CharacterListItem) => adminApi.regenerateCharacter(c.stockId),
    onSuccess: () => ok("Regeneration queued — the character is pending again."),
    onError: (err) => fail(err, "Could not regenerate"),
  });

  // Re-embed a character into the casting index (vms_characters). Activate auto-indexes; this is for
  // re-indexing after editing attributes, or forcing an index if the embedder was down at activate.
  const reindex = useMutation({
    mutationFn: (c: CharacterListItem) => adminApi.reindexCharacter(c.stockId),
    onSuccess: (r) => ok(r.ok ? "Re-indexed into the casting search." : `Not indexed: ${r.error ?? "embedder off"}`),
    onError: (err) => fail(err, "Could not reindex"),
  });

  const generate = useMutation({
    mutationFn: () => {
      const { displayName, voiceCatalogId, visualDescriptor, niche, physicalAttributes, personas, ...gen } = genForm;
      // Drop empty selections so we only send chosen attributes.
      const physical = Object.fromEntries(
        Object.entries(physicalAttributes ?? {}).filter(([, v]) => v),
      );
      return adminApi.createCharacterGenerate({
        generation: {
          ...gen,
          ...(niche ? { niche } : {}),
          ...(visualDescriptor?.trim() ? { visualDescriptor: visualDescriptor.trim() } : {}),
          ...(Object.keys(physical).length ? { physicalAttributes: physical } : {}),
          ...(personas?.length ? { personas } : {}),
        },
        displayName: displayName.trim() || undefined,
        voiceCatalogId,
      });
    },
    onSuccess: () => {
      setShowGenerate(false);
      setGenForm(emptyGenForm);
      ok("Character queued — it will generate and appear as ready.");
    },
    onError: (err) => fail(err, "Could not queue the character"),
  });

  const characters = data?.characters ?? [];
  const total = data?.pagination.total ?? 0;
  const offset = filters.offset ?? 0;

  const setFilter = (patch: Partial<CharacterBrowseFilters>) =>
    setFilters((f) => ({ ...f, ...patch, offset: 0 }));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-surface-900">Characters</h1>
          <p className="text-[13px] text-surface-500">
            {total} character{total === 1 ? "" : "s"} in the library
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setCastingArc({ circleKey: "", label: "ad-hoc (type a persona)" })}>
            <Search className="h-4 w-4" /> Cast an arc
          </Button>
          <Button variant="outline" onClick={() => setShowRoster(true)}>
            <Users className="h-4 w-4" /> Cast for a job
          </Button>
          <Button onClick={() => { setGenForm(emptyGenForm); setShowGenerate(true); }}>
            <Plus className="h-4 w-4" /> Generate character
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 py-3">
          <Select
            label="Gender"
            value={filters.gender ?? ""}
            onChange={(e) => setFilter({ gender: e.target.value || undefined })}
            options={[{ value: "", label: "Any" }, { value: "F", label: "Female" }, { value: "M", label: "Male" }]}
          />
          <Select
            label="State"
            value={filters.state ?? ""}
            onChange={(e) => setFilter({ state: (e.target.value || undefined) as CharacterBrowseFilters["state"] })}
            options={[{ value: "", label: "Any" }, { value: "ready", label: "Ready" }, { value: "pending", label: "Pending" }, { value: "failed", label: "Failed" }]}
          />
          <Select
            label="Capability"
            value={filters.capability ?? ""}
            onChange={(e) => setFilter({ capability: (e.target.value || undefined) as CharacterBrowseFilters["capability"] })}
            options={[{ value: "", label: "Any" }, { value: "talking_head", label: "Talking head" }, { value: "motion", label: "Motion" }]}
          />
          <Select
            label="Visibility"
            value={filters.visibility ?? ""}
            onChange={(e) => setFilter({ visibility: (e.target.value || undefined) as CharacterBrowseFilters["visibility"] })}
            options={[{ value: "", label: "Any" }, { value: "public", label: "Public" }, { value: "private", label: "Private" }]}
          />
          <div className="min-w-[180px] flex-1">
            <Input
              label="Search"
              placeholder="Name…"
              value={filters.search ?? ""}
              onChange={(e) => setFilter({ search: e.target.value || undefined })}
            />
          </div>
        </CardContent>
      </Card>

      {notice && <Card><CardContent className="py-3 text-sm text-emerald-700">{notice}</CardContent></Card>}
      {actionError && <Card><CardContent className="py-3 text-sm text-red-700">{actionError}</CardContent></Card>}

      {isLoading ? (
        <div className="flex justify-center py-16"><Spinner /></div>
      ) : characters.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-sm text-surface-500">
          No characters match these filters.
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
          {characters.map((c) => (
            <Card key={c.stockId} className="overflow-hidden">
              <button
                className="block w-full text-left"
                onClick={() => setDetailId(c.stockId)}
                title="View details"
              >
                <div className="relative aspect-[3/4] bg-surface-100">
                  {c.previewUrl ? (
                    <img src={c.previewUrl} alt={c.displayName ?? "character"} className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center">
                      {c.state === "pending"
                        ? <Loader2 className="h-8 w-8 animate-spin text-surface-400" />
                        : <ImageIcon className="h-8 w-8 text-surface-300" />}
                    </div>
                  )}
                  <div className="absolute left-2 top-2">
                    <Badge variant={STATE_VARIANT[c.state] ?? "muted"}>{c.state}</Badge>
                  </div>
                  {c.isDefault && (
                    <div className="absolute right-2 top-2">
                      <Badge variant="info"><Star className="mr-1 h-3 w-3" /> default</Badge>
                    </div>
                  )}
                </div>
              </button>
              <CardContent className="space-y-2 py-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="truncate text-[13px] font-medium text-surface-900">
                    {c.displayName ?? "Unnamed"}
                  </div>
                  <div className="text-[11px] text-surface-400">{c.gender ?? "—"}</div>
                </div>
                <div className="flex flex-wrap gap-1">
                  {c.capability.talkingHead && <Badge variant="default">talking head</Badge>}
                  {c.capability.motion && <Badge variant="default">motion</Badge>}
                  {!c.isEnabled && <Badge variant="muted">disabled</Badge>}
                  {c.isDelisted && <Badge variant="muted">delisted</Badge>}
                </div>
                <div className="flex flex-wrap gap-1 pt-1">
                  <Button size="sm" variant="ghost" title={c.isEnabled ? "Disable" : "Enable"}
                    loading={activate.isPending && activate.variables?.stockId === c.stockId}
                    onClick={() => activate.mutate(c)}>
                    <Power className="h-3.5 w-3.5" /> {c.isEnabled ? "Disable" : "Enable"}
                  </Button>
                  <Button size="sm" variant="ghost" title={c.isDelisted ? "Relist" : "Delist"}
                    onClick={() => delist.mutate(c)}>
                    {c.isDelisted ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                    {c.isDelisted ? "Relist" : "Delist"}
                  </Button>
                  {c.state === "ready" && !c.isDefault && c.gender && (
                    <Button size="sm" variant="ghost" title="Set gender default"
                      onClick={() => makeDefault.mutate(c)}>
                      <Star className="h-3.5 w-3.5" /> Default
                    </Button>
                  )}
                  {(c.state === "ready" || c.state === "failed") && (
                    <Button size="sm" variant="ghost" title="Regenerate sheet"
                      onClick={() => regenerate.mutate(c)}>
                      <RefreshCw className="h-3.5 w-3.5" /> Regenerate
                    </Button>
                  )}
                  {c.state === "ready" && (
                    <Button size="sm" variant="ghost" title="Re-embed into casting search (vms_characters)"
                      loading={reindex.isPending && reindex.variables?.stockId === c.stockId}
                      onClick={() => reindex.mutate(c)}>
                      <Search className="h-3.5 w-3.5" /> Reindex
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between">
          <Button variant="outline" size="sm" disabled={offset === 0}
            onClick={() => setFilters((f) => ({ ...f, offset: Math.max(0, (f.offset ?? 0) - PAGE_SIZE) }))}>
            Previous
          </Button>
          <span className="text-[12px] text-surface-500">
            {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
          </span>
          <Button variant="outline" size="sm" disabled={offset + PAGE_SIZE >= total}
            onClick={() => setFilters((f) => ({ ...f, offset: (f.offset ?? 0) + PAGE_SIZE }))}>
            Next
          </Button>
        </div>
      )}

      {detailId && <CharacterDetailModal id={detailId} onClose={() => setDetailId(null)} />}

      {castingArc && (
        <CastPanel
          circleKey={castingArc.circleKey}
          arcLabel={castingArc.label}
          seed={castingArc.seed}
          onClose={() => setCastingArc(null)}
          onCreateCharacter={openGenerateFromPersona}
          onViewCharacter={(id) => { setCastingArc(null); setDetailId(id); }}
        />
      )}

      <GenerateModal
        open={showGenerate}
        form={genForm}
        setForm={setGenForm}
        submitting={generate.isPending}
        onClose={() => setShowGenerate(false)}
        onSubmit={() => generate.mutate()}
      />

      {showRoster && (
        <RosterModal
          onClose={() => setShowRoster(false)}
          onView={(id) => { setShowRoster(false); setDetailId(id); }}
        />
      )}
    </div>
  );
}

// ── Detail modal ─────────────────────────────────────────────────────────────

function CharacterDetailModal({ id, onClose }: { id: string; onClose: () => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "character", id],
    queryFn: () => adminApi.getCharacter(id),
  });

  const c = data?.character;

  return (
    <Modal open onClose={onClose} size="lg" title={c?.displayName ?? "Character"}
      description={c ? `${c.gender ?? "—"} · ${c.ageRange ?? "—"} · ${c.style ?? "—"} · ${c.state}` : undefined}>
      {isLoading || !data ? (
        <div className="flex justify-center py-10"><Spinner /></div>
      ) : (
        <div className="space-y-5">
          <div className="flex flex-wrap gap-1">
            {data.capability.talkingHead && <Badge variant="success">talking head (HeyGen)</Badge>}
            {data.capability.motion && <Badge variant="success">motion (Kling)</Badge>}
            {data.heygen && <Badge variant="muted">look {data.heygen.providerAvatarId}</Badge>}
          </div>

          {/* (session 9) Voice — the character's own; every video it's in speaks with it */}
          <CharacterVoice id={id} gender={c?.gender ?? null} current={data.voice ?? null} />

          {/* Reference sheet */}
          <div>
            <div className="mb-2 text-[12px] font-medium text-surface-700">
              Reference sheet ({data.klingRefs.length})
            </div>
            {data.klingRefs.length === 0 ? (
              <p className="text-[13px] text-surface-500">No reference frames yet.</p>
            ) : (
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
                {data.klingRefs.map((ref) => (
                  <div key={ref.position} className="overflow-hidden rounded-lg border border-surface-200">
                    <img src={ref.url} alt={ref.type} className="aspect-[3/4] w-full object-cover" />
                    <div className="truncate px-1 py-0.5 text-center text-[10px] text-surface-500">
                      {ref.type}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Attributes */}
          <div>
            <div className="mb-2 text-[12px] font-medium text-surface-700">
              Attributes ({data.attributes.length})
            </div>
            {data.attributes.length === 0 ? (
              <p className="text-[13px] text-surface-500">No attributes.</p>
            ) : (
              <div className="flex flex-wrap gap-1">
                {data.attributes.map((a) => (
                  <Badge key={a.key} variant="default">
                    {a.key}: {a.valueText ?? a.valueNum ?? (a.valueBool === null ? "—" : String(a.valueBool))}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

// ── Generate modal ───────────────────────────────────────────────────────────

function GenerateModal({
  open, form, setForm, submitting, onClose, onSubmit,
}: {
  open: boolean;
  form: typeof emptyGenForm;
  setForm: (f: typeof emptyGenForm) => void;
  submitting: boolean;
  onClose: () => void;
  onSubmit: () => void;
}) {
  const { data: voiceData } = useQuery({
    queryKey: ["admin", "voices"],
    queryFn: () => adminApi.listVoices(),
    enabled: open,
  });
  const { data: nicheData } = useQuery({
    queryKey: ["admin", "niches"],
    queryFn: () => adminApi.listNiches(),
    enabled: open,
  });
  const { data: attrCatalog } = useQuery({
    queryKey: ["admin", "attributes-catalog"],
    queryFn: () => adminApi.listAttributesCatalog(),
    enabled: open,
  });
  const { data: personaData } = useQuery({
    queryKey: ["admin", "personas"],
    queryFn: () => adminApi.listPersonas(),
    enabled: open,
  });
  const selectedPersonas = form.personas ?? [];
  const togglePersona = (key: string) =>
    setForm({
      ...form,
      personas: selectedPersonas.includes(key)
        ? selectedPersonas.filter((p) => p !== key)
        : [...selectedPersonas, key],
    });
  // Dimensions applicable to the chosen gender (drop gender-restricted ones that
  // don't match, e.g. facial_hair for F).
  const dimensions = (attrCatalog?.dimensions ?? []).filter(
    (d) => !d.gender || d.gender === form.gender,
  );
  const setAttr = (key: string, value: string) =>
    setForm({ ...form, physicalAttributes: { ...(form.physicalAttributes ?? {}), [key]: value } });
  const nicheOptions = [
    { value: "", label: "None" },
    ...(nicheData?.niches ?? []).map((n) => ({ value: n.key, label: n.label })),
  ];
  const voices = voiceData?.voices ?? [];
  // Voices matching the chosen gender first — a HeyGen talking head reads better
  // with a matching voice, and the seeder resolves per gender too.
  const voiceOptions = [
    { value: "", label: voices.length ? "Select a voice…" : "No active voices" },
    ...voices
      .filter((v) => !form.gender || v.gender === form.gender)
      .map((v) => ({ value: v.id, label: `${v.name} (${v.provider})` })),
  ];

  const canSubmit = form.ethnicity.trim() !== "" && form.voiceCatalogId !== "" && !submitting;

  return (
    <Modal open={open} onClose={onClose} title="Generate character"
      description="Attributes drive a 6-angle sheet; both provider identities are minted, then the character becomes ready."
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button onClick={onSubmit} loading={submitting} disabled={!canSubmit}>Queue generation</Button>
        </div>
      }>
      <div className="space-y-3">
        <Input label="Display name (optional)" value={form.displayName}
          onChange={(e) => setForm({ ...form, displayName: e.target.value })} placeholder="Ava" />
        <div className="grid grid-cols-2 gap-3">
          <Select label="Gender" value={form.gender}
            onChange={(e) => setForm({ ...form, gender: e.target.value as "F" | "M", voiceCatalogId: "" })}
            options={[{ value: "F", label: "Female" }, { value: "M", label: "Male" }]} />
          <Select label="Age range" value={form.ageRange}
            onChange={(e) => setForm({ ...form, ageRange: e.target.value })}
            options={["18-25", "25-35", "35-45", "45-55"].map((v) => ({ value: v, label: v }))} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Ethnicity" value={form.ethnicity}
            onChange={(e) => setForm({ ...form, ethnicity: e.target.value })} placeholder="East Asian" />
          <Select label="Style" value={form.style}
            onChange={(e) => setForm({ ...form, style: e.target.value })}
            options={["casual", "polished", "professional"].map((v) => ({ value: v, label: v }))} />
        </div>
        <Select label="Voice" value={form.voiceCatalogId}
          onChange={(e) => setForm({ ...form, voiceCatalogId: e.target.value })}
          options={voiceOptions}
          hint="Active voices for the selected gender." />
        <Select label="Niche (optional)" value={form.niche ?? ""}
          onChange={(e) => setForm({ ...form, niche: e.target.value })}
          options={nicheOptions}
          hint="Applies a curated look for that content vertical." />

        {(personaData?.personas?.length ?? 0) > 0 && (
          <div>
            <div className="mb-1 text-[13px] font-medium text-surface-700">Personas (optional)</div>
            <p className="mb-2 text-[11px] text-surface-500">
              Castable archetypes — a character can hold several, making them reusable across verticals.
              These shape the look and are matched when casting.
            </p>
            <div className="flex flex-wrap gap-1.5">
              {personaData!.personas.map((p) => {
                const on = selectedPersonas.includes(p.key);
                return (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => togglePersona(p.key)}
                    className={
                      "rounded-full border px-2.5 py-1 text-[12px] transition-colors " +
                      (on
                        ? "border-brand-500 bg-brand-50 text-brand-700"
                        : "border-surface-200 text-surface-600 hover:border-surface-300")
                    }
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        <Input label="Visual descriptor (optional — overrides niche look)" value={form.visualDescriptor ?? ""}
          onChange={(e) => setForm({ ...form, visualDescriptor: e.target.value })}
          placeholder="relaxed lifestyle look, soft neutral knitwear" />

        {dimensions.length > 0 && (
          <div className="border-t border-surface-200 pt-3">
            <div className="mb-2 text-[12px] font-medium text-surface-700">
              Physical attributes (optional)
            </div>
            <p className="mb-3 text-[11px] text-surface-500">
              Chosen values shape the generated face and become searchable tags for casting.
            </p>
            <div className="grid grid-cols-2 gap-3">
              {dimensions.map((d) => (
                <Select
                  key={d.key}
                  label={d.label}
                  value={form.physicalAttributes?.[d.key] ?? ""}
                  onChange={(e) => setAttr(d.key, e.target.value)}
                  options={[
                    { value: "", label: "Any" },
                    ...d.values.map((v) => ({ value: v.key, label: v.label })),
                  ]}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ── Roster modal — "Cast for a job" (Phase 8) ────────────────────────────────

function RosterModal({
  onClose, onView,
}: {
  onClose: () => void;
  onView: (stockId: string) => void;
}) {
  const [vertical, setVertical] = useState("");
  const [gender, setGender] = useState("");
  const [picked, setPicked] = useState<string | null>(null);

  const { data: verticalData } = useQuery({
    queryKey: ["admin", "verticals"],
    queryFn: () => adminApi.listVerticals(),
  });
  const verticalOptions = [
    { value: "", label: "Select a business type…" },
    ...(verticalData?.verticals ?? []).map((v) => ({ value: v.key, label: v.label })),
  ];

  const { data: roster, isFetching } = useQuery({
    queryKey: ["admin", "roster", vertical, gender],
    queryFn: () => adminApi.castRoster({ vertical, gender: gender || undefined, limit: 24 }),
    enabled: vertical !== "",
  });

  // Auto-select the top pick when a roster loads.
  const autoPick = roster?.autoPick ?? null;
  const effectivePick = picked ?? autoPick;

  return (
    <Modal open onClose={onClose} size="lg" title="Cast for a job"
      description="Pick a business type — we cast the best-fit characters, ranked like a lineup. Keep the top pick or swap.">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Select label="Business type / vertical" value={vertical}
            onChange={(e) => { setVertical(e.target.value); setPicked(null); }}
            options={verticalOptions} />
          <Select label="Gender (optional)" value={gender}
            onChange={(e) => { setGender(e.target.value); setPicked(null); }}
            options={[{ value: "", label: "Any" }, { value: "F", label: "Female" }, { value: "M", label: "Male" }]} />
        </div>

        {vertical === "" ? (
          <p className="py-6 text-center text-[13px] text-surface-500">
            Choose a business type to see the casting lineup.
          </p>
        ) : isFetching ? (
          <div className="flex justify-center py-10"><Spinner /></div>
        ) : (roster?.roster.length ?? 0) === 0 ? (
          <Card><CardContent className="py-8 text-center text-sm text-surface-500">
            No ready characters match this vertical yet. Generate characters with matching personas
            {roster?.personas?.length ? ` (${roster.personas.join(", ")})` : ""}.
          </CardContent></Card>
        ) : (
          <>
            <div className="text-[12px] text-surface-500">
              Casting for personas: {roster!.personas.join(", ")}
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {roster!.roster.map((c, i) => {
                const isPick = effectivePick === c.stockId;
                return (
                  <Card key={c.stockId}
                    className={"overflow-hidden " + (isPick ? "ring-2 ring-brand-500" : "")}>
                    <button className="block w-full text-left" onClick={() => setPicked(c.stockId)}>
                      <div className="relative aspect-[3/4] bg-surface-100">
                        {c.previewUrl
                          ? <img src={c.previewUrl} alt={c.displayName ?? ""} className="h-full w-full object-cover" />
                          : <div className="flex h-full w-full items-center justify-center"><ImageIcon className="h-7 w-7 text-surface-300" /></div>}
                        {i === 0 && !picked && (
                          <div className="absolute left-1.5 top-1.5"><Badge variant="success">top pick</Badge></div>
                        )}
                        {isPick && (
                          <div className="absolute right-1.5 top-1.5 rounded-full bg-brand-500 p-1 text-white">
                            <Check className="h-3.5 w-3.5" />
                          </div>
                        )}
                      </div>
                    </button>
                    <CardContent className="space-y-1 py-2">
                      <div className="flex items-center justify-between gap-1">
                        <span className="truncate text-[12px] font-medium text-surface-900">{c.displayName ?? "Unnamed"}</span>
                        <span className="text-[10px] text-surface-400">{c.personaMatches} match{c.personaMatches === 1 ? "" : "es"}</span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {c.matchedPersonas.slice(0, 2).map((p) => (
                          <Badge key={p} variant="default">{p.replace(/_/g, " ")}</Badge>
                        ))}
                      </div>
                      <button
                        className="mt-1 text-[11px] text-brand-600 hover:underline"
                        onClick={() => onView(c.stockId)}
                      >
                        View details
                      </button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            {effectivePick && (
              <div className="rounded-lg border border-surface-200 bg-surface-50 px-3 py-2 text-[13px] text-surface-700">
                Selected: <strong>{roster!.roster.find((c) => c.stockId === effectivePick)?.displayName ?? effectivePick}</strong>
                {" "}— this character will front the job.
                {picked && picked !== autoPick && <span className="ml-1 text-surface-400">(swapped from the top pick)</span>}
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}


/** (session 10, drop 114b; owner 11:29) a voice's preview: ElevenLabs' own, from its preview host; missing or no longer
 *  loading → the voice is shown without one (never our copy). */
function VoicePreview({ url }: { url: string | null | undefined }) {
  const [dead, setDead] = useState(false);
  const playable = dead ? null : playablePreviewUrl(url);
  return playable ? <audio controls preload="none" src={playable} onError={() => setDead(true)} className="h-8" />
    : <span className="text-[12px] text-surface-500">(no preview)</span>;
}

/**
 * (session 9, owner 2026-10-07) A character's VOICE: shown with its preview, and changeable. ElevenLabs voices, the
 * character's gender first. Saved through the identity service (PUT /admin/characters/:id/voice); the next video uses it
 * for the narration and the presenter.
 */
function CharacterVoice({ id, gender, current }: { id: string; gender: string | null; current: (import("@/api/admin.ts").VoiceOption & { status: string }) | null }) {
  const qc = useQueryClient();
  const [picked, setPicked] = useState<string>("");
  const { data: voiceData } = useQuery({ queryKey: ["admin", "voices"], queryFn: () => adminApi.listVoices() });
  const g = (gender ?? "").toUpperCase().startsWith("M") ? "M" : (gender ?? "").toUpperCase().startsWith("F") ? "F" : null;
  const voices = (voiceData?.voices ?? []).filter((v) => v.provider === "elevenlabs")
    .sort((a, b) => Number(b.gender === g) - Number(a.gender === g) || a.name.localeCompare(b.name));
  const chosen = voices.find((v) => v.id === picked) ?? null;
  const save = useMutation({
    mutationFn: () => adminApi.setCharacterVoice(id, picked),
    onSuccess: () => { setPicked(""); void qc.invalidateQueries({ queryKey: ["admin", "character", id] }); },
  });
  const label = (v: { name: string; tone?: string | null; accent?: string | null; gender: string }) =>
    `${v.name}${v.tone ? ` — ${v.tone}` : ""}${v.accent ? ` (${v.accent})` : ""}${g && v.gender !== g ? ` · ${v.gender}` : ""}`;
  return (
    <div>
      <div className="mb-2 text-[12px] font-medium text-surface-700">Voice</div>
      {current ? (
        <div className="flex flex-wrap items-center gap-2 text-[13px]">
          <Badge variant={current.provider === "elevenlabs" ? "success" : "muted"}>{current.provider}</Badge>
          <span className="font-medium">{current.name}</span>
          {current.tone && <span className="text-surface-500">{current.tone}</span>}
          <VoicePreview key={`${current.id}:${current.sampleUrl ?? ""}`} url={current.sampleUrl} />
        </div>
      ) : (
        <p className="text-[13px] text-amber-700">No voice — videos with this character use a style voice.</p>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <select className="rounded border border-surface-300 px-2 py-1 text-[13px]" value={picked} onChange={(e) => setPicked(e.target.value)}>
          <option value="">{voices.length ? "Change voice…" : "No ElevenLabs voices in the catalogue yet"}</option>
          {voices.map((v) => <option key={v.id} value={v.id} disabled={v.id === current?.id}>{label(v)}</option>)}
        </select>
        {chosen && <VoicePreview key={chosen.id} url={chosen.sampleUrl} />}
        <Button size="sm" disabled={!picked || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? "Saving…" : "Save voice"}
        </Button>
        {save.isError && <span className="text-[12px] text-red-600">{(save.error as Error).message}</span>}
      </div>
    </div>
  );
}
